import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2 } from "lucide-react";
import { useFamily } from "@/hooks/useFamily";
import { useTaskLists } from "@/hooks/useTaskLists";
import { useTaskListMembers } from "@/hooks/useTaskListMembers";
import { useCurrentSection } from "@/hooks/useCurrentSection";
import { useUniversalContacts, UniversalContact } from "@/hooks/useUniversalContacts";
import { useToast } from "@/hooks/use-toast";
import { TaskListMembersSelector } from "./TaskListMembersSelector";
import { PageHeader } from "@/components/PageHeader";
import { NoFamilyMessage } from "@/components/NoFamilyMessage";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
const ICON_OPTIONS = ["📝", "🛒", "🏠", "🎓", "💼", "💳", "🎯", "📋", "✅", "📚", "🎨", "🔧", "🍽️", "🧼", "🌟", "💡"];
interface TaskListFormProps {
  mode: 'create' | 'edit';
  listId?: string;
}
export function TaskListForm({
  mode,
  listId
}: TaskListFormProps) {
  const navigate = useNavigate();
  const {
    family
  } = useFamily();
  const {
    taskLists,
    createTaskList,
    updateTaskList,
    deleteTaskList,
    addTaskListMember,
    removeTaskListMember
  } = useTaskLists(family?.id);
  const {
    data: currentMembers,
    isLoading: membersLoading
  } = useTaskListMembers(listId);
  const {
    primaryVariant,
    secondaryVariant
  } = useCurrentSection();
  const {
    groupedContacts,
    loading: contactsLoading,
    getProfileId
  } = useUniversalContacts(family?.id);
  const {
    toast
  } = useToast();
  const taskList = mode === 'edit' ? taskLists.find(list => list.id === listId) : null;

  // Show all contacts - we'll handle profile linking in the backend
  const eligibleGroupedContacts = useMemo(() => {
    return groupedContacts; // Show all contacts, not just those with profiles
  }, [groupedContacts]);
  const allEligibleContacts: UniversalContact[] = useMemo(() => Object.values(eligibleGroupedContacts).flat(), [eligibleGroupedContacts]);
  const [name, setName] = useState("");
  const [selectedIcon, setSelectedIcon] = useState("📝");
  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Pre-fill form data in edit mode
  useEffect(() => {
    if (mode === 'edit' && taskList) {
      setName(taskList.name);
      setSelectedIcon(taskList.icon);
    }
  }, [mode, taskList]);

  // Pre-fill selected members in edit mode (only once)
  useEffect(() => {
    if (mode === 'edit' && currentMembers && allEligibleContacts.length > 0 && !prefilled && !contactsLoading) {
      console.log('Pre-filling members:', currentMembers);
      console.log('All eligible contacts:', allEligibleContacts);
      const ids = currentMembers.map(m => {
        const contact = allEligibleContacts.find(c => getProfileId(c) === m.profile_id);
        console.log(`Looking for profile_id ${m.profile_id}, found contact:`, contact);
        return contact?.id;
      }).filter(Boolean) as string[];
      console.log('Mapped contact IDs:', ids);
      setSelectedContacts(ids);
      setPrefilled(true);
    }
  }, [mode, currentMembers, allEligibleContacts, prefilled, getProfileId, contactsLoading]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setIsSubmitting(true);
    try {
      if (mode === 'create') {
        // Create new list
        const memberData = selectedContacts.map(contactId => {
          const contact = allEligibleContacts.find(c => c.id === contactId);
          if (!contact) return null;
          const profileId = getProfileId(contact);
          if (profileId) {
            return {
              profile_id: profileId
            };
          }
          return null;
        }).filter(Boolean) as {
          profile_id: string;
        }[];
        await createTaskList(name.trim(), selectedIcon);
        navigate("/tasks");
      } else {
        // Update existing list
        if (!listId || !taskList) return;

        // Update list name and icon if changed
        if (name !== taskList.name || selectedIcon !== taskList.icon) {
          await updateTaskList(listId, name, selectedIcon);
        }

        // Calculate member changes
        const currentMemberIds = (currentMembers || []).map(m => m.profile_id);
        const selectedProfileIds = selectedContacts.map(contactId => {
          const contact = allEligibleContacts.find(c => c.id === contactId);
          return contact ? getProfileId(contact) : null;
        }).filter(Boolean) as string[];
        const toAdd = selectedProfileIds.filter(id => !currentMemberIds.includes(id));
        const toRemove = currentMemberIds.filter(id => !selectedProfileIds.includes(id));

        // Apply member changes
        const memberPromises: Promise<void>[] = [];
        toAdd.forEach(profileId => {
          memberPromises.push(new Promise((resolve, reject) => {
            try {
              addTaskListMember(listId, profileId);
              resolve();
            } catch (error) {
              reject(error);
            }
          }));
        });
        toRemove.forEach(profileId => {
          memberPromises.push(new Promise((resolve, reject) => {
            try {
              removeTaskListMember(listId, profileId);
              resolve();
            } catch (error) {
              reject(error);
            }
          }));
        });
        await Promise.all(memberPromises);
        navigate(`/task-list/${listId}`);
      }
    } catch (error) {
      console.error(`Error ${mode === 'create' ? 'creating' : 'updating'} task list:`, error);
      toast({
        title: "Erreur",
        description: `Impossible de ${mode === 'create' ? 'créer' : 'sauvegarder'} la liste`,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleDelete = async () => {
    if (!listId) return;
    setIsDeleting(true);
    try {
      await deleteTaskList(listId);
      navigate("/tasks");
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de supprimer la liste",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Loading state for edit mode
  if (mode === 'edit' && (membersLoading || !taskList)) {
    if (!taskList) {
      return <div className="min-h-screen bg-page-gradient flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Liste introuvable</h2>
            <Button onClick={() => navigate("/tasks")}>
              Retour aux listes
            </Button>
          </div>
        </div>;
    }
    return <div className="min-h-screen bg-page-gradient flex items-center justify-center">
        <div className="text-center text-muted-foreground">Chargement...</div>
      </div>;
  }
  if (!family) {
    return <NoFamilyMessage description="Vous devez d'abord créer une famille pour organiser vos listes de tâches" buttonText="Créer ma famille" variant="tasks" />;
  }
  return <div className="min-h-screen bg-page-gradient">
      <PageHeader title={mode === 'create' ? "Nouvelle liste" : "Modifier la liste"} backPath={mode === 'create' ? "/tasks" : `/task-list/${listId}`} />
      <div className="container mx-auto px-4">
        <form onSubmit={handleSubmit} className="space-y-6 max-w-md mx-auto pt-6 pb-40">
          <div className="space-y-2">
            <Label htmlFor="name">Nom de la liste</Label>
            <Input id="name" type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Ex: Courses, Maison, École..." required />
          </div>

          <div className="space-y-3">
            <Label>Icône</Label>
            <div className="grid grid-cols-8 gap-2">
              {ICON_OPTIONS.map(icon => <Button key={icon} type="button" variant={selectedIcon === icon ? primaryVariant as any : secondaryVariant as any} size="sm" onClick={() => setSelectedIcon(icon)} className="h-12 text-xl">
                  {icon}
                </Button>)}
            </div>
          </div>

          {Object.keys(eligibleGroupedContacts).length > 0 && <div className="space-y-3">
              <Label>Qui doit voir </Label>
              <TaskListMembersSelector contacts={allEligibleContacts} selectedContactIds={selectedContacts} onSelectionChange={setSelectedContacts} getProfileId={getProfileId} placeholder="Sélectionner les membres" />
            </div>}

          {/* Danger Zone - Only in edit mode */}
          {mode === 'edit' && <div className="space-y-3 p-4 border rounded-lg bg-gradient-to-r from-destructive/5 to-destructive/10 mb-28">
              <div className="flex items-center gap-2">
                <Trash2 className="h-4 w-4 text-destructive" />
                <Label className="font-medium text-destructive">Zone de danger</Label>
              </div>
              <p className="text-sm text-muted-foreground">
                La suppression de cette liste supprimera définitivement toutes les tâches qu'elle contient.
              </p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="gap-2" disabled={isDeleting}>
                    <Trash2 className="h-4 w-4" />
                    {isDeleting ? "Suppression..." : "Supprimer la liste"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer la liste ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est irréversible. Toutes les tâches de cette liste seront également supprimées.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      {isDeleting ? 'Suppression...' : 'Supprimer définitivement'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>}

          <div className="fixed bottom-0 left-0 right-0 p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] border-t bg-background/95 backdrop-blur-sm space-y-2 z-10">
            <Button type="submit" variant={primaryVariant as any} className="w-full" disabled={!name.trim() || isSubmitting || contactsLoading}>
              {isSubmitting ? mode === 'create' ? "Création..." : "Sauvegarde..." : mode === 'create' ? "Créer la liste" : "Sauvegarder"}
            </Button>
            
            <Button type="button" variant="outline" className="w-full hover:bg-muted hover:text-muted-foreground" onClick={() => navigate(mode === 'create' ? "/tasks" : `/task-list/${listId}`)}>
              Annuler
            </Button>
          </div>
        </form>
      </div>
    </div>;
}